import * as React from 'react';
import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import Helmet from '../../../commonComponents/Helmet';
import Tile from '../../common/Tile';
import PublicAutoLogoutLayout, { SubTitleParagraph, TitleParagraph } from './common/PublicLayout';
import PublicPageSteps from './common/PublicPageSteps';
import { publicClientCompletionPageUrl, publicClientInterestsPageUrl } from '../../../core/constants';
import PublicFormButtons from './common/PublicFormButtons';
import { useProgress, useRequireProgress } from './common/PublicProgressContext';
import {
	ctxTermsAccepted,
	ctxClientTypeSelected,
	ctxClientBasicInfoCompleted,
	ctxClientProfileReviewed
} from './common/publicConstants';
import useAsyncAction from '../../../commonComponents/Hooks';
import { completePublicClientProfiling, getPublicClientProfileSummary } from '../../../core/api/publicClientApi';
import { PublicClientProfileSummary } from '../../../models/PublicClientModels';
import { blackColour, tabletPortraitMinWidth } from '../../cssConstants';
import { useNavigateWithAppName } from '../../common/NavigateWithAppName';

const PublicProfileReviewPage = () => {
	const navigate = useNavigateWithAppName();
	const { clientBasicInfoCompleted, updateProgress } = useProgress();
	const [currentStep] = useState<number>(5);
	const performAsyncAction = useAsyncAction();
	const { signal } = new AbortController();
	const [clientProfile, setClientProfile] = useState<PublicClientProfileSummary>({
		basicInfo: [],
		familyMembers: [],
		entities: []
	});

	// Ensure the user has accepted the terms, selected a client type and has new client guid assigned to access this page.
	useRequireProgress([ctxTermsAccepted, ctxClientTypeSelected, ctxClientBasicInfoCompleted]);

	useEffect(() => {
		if (clientBasicInfoCompleted) {
			performAsyncAction(async () => {
				const profileSummary = await getPublicClientProfileSummary(signal);
				setClientProfile(profileSummary);
			});
		}
	}, []);

	const handleBackButtonClick = () => {
		navigate(publicClientInterestsPageUrl);
	};

	const handleContinueButtonClick = () => {
		performAsyncAction(async () => {
			await completePublicClientProfiling(signal);
		});

		updateProgress(ctxClientProfileReviewed, true);
		navigate(publicClientCompletionPageUrl);
	};

	return (
		<PublicAutoLogoutLayout>
			<Helmet>
				<title>Join Kelly+Partners | Prospective Client</title>
			</Helmet>
			<Tile>
				<PublicPageSteps currentStep={currentStep} />
				<TitleParagraph>Review Your Details</TitleParagraph>
				<SubTitleParagraph>Please confirm your details below before submitting your application.</SubTitleParagraph>
				<StyledHeader>Basic Information</StyledHeader>
				{clientProfile.basicInfo.map((info) => (
					<Row key={info.key}>
						<Label>{info.key}:</Label>
						<Value>{info.value}</Value>
					</Row>
				))}

				{clientProfile.familyMembers && (
					<>
						<StyledHeader>Family Members</StyledHeader>
						{clientProfile.familyMembers.map((info) => (
							<Row key={info.key}>
								<Label>{info.key}:</Label>
								<Value>{info.value}</Value>
							</Row>
						))}
					</>
				)}
				{clientProfile.entities && (
					<>
						<StyledHeader>Business Entities</StyledHeader>
						{clientProfile.entities.map((info) => (
							<Row key={info.key}>
								<Label>{info.key}:</Label>
								<Value>{info.value}</Value>
							</Row>
						))}
					</>
				)}
				<PublicFormButtons
					onBackButtonClick={handleBackButtonClick}
					onContinueButtonClick={handleContinueButtonClick}
					continueButtonText="Submit"
				/>
			</Tile>
		</PublicAutoLogoutLayout>
	);
};

export default PublicProfileReviewPage;

const Row = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 12px 0;
	border-bottom: 1px solid #ccc;

	&:first-child {
		border-top: 1px solid #ccc;
	}

	&:has(+ p) {
		border-bottom: none; /* Remove the border if the next sibling is a <p> */
	}
`;

const Label = styled.div`
	font-weight: bold;
	text-align: left;
	flex: 1;
`;

const Value = styled.div`
	text-align: right;
	flex: 1;

	@media (min-width: ${tabletPortraitMinWidth}) {
		padding-right: 50px;
	}
`;

const StyledHeader = styled.p`
	color: ${blackColour};
	font-weight: bold;
	padding: 10px 0;
	border-top: 1px solid #000;
	border-bottom: 1px solid #000;
	margin-top: 20px;
`;
